import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AlertTriangle,
  MessageSquare,
  Zap,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Building2,
  X,
  Activity,
  Phone,
  Calendar,
  Flame,
  Clock
} from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { wasNeverSent } from '../lib/messageVisibility';
import { useAuth } from '../context/AuthContext';
import { useTenants } from './AIControlRoomAdaptive/hooks/useTenants';

// ─── Constants ────────────────────────────────────────────────────────────────

const TERMINAL_INTENTS = new Set([
  'OPT_OUT', 'NOT_INTERESTED', 'WRONG_NUMBER', 'HOSTILE'
]);

const INTENT_CONFIG = {
  FALLBACK: {
    label: 'AI Unsure',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    bar: 'bg-yellow-400',
    icon: Brain,
    hint: 'Low confidence — AI is guessing'
  },
  INCOHERENT: {
    label: 'Incoherent',
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    bar: 'bg-purple-400',
    icon: AlertTriangle,
    hint: 'May be a real lead, misclassified'
  },
  POSITIVE: {
    label: 'Positive',
    color: 'bg-green-100 text-green-800 border-green-300',
    bar: 'bg-green-400',
    icon: Zap,
    hint: 'Buying signal — is AI capitalizing?'
  },
  NEUTRAL: {
    label: 'Neutral',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    bar: 'bg-gray-400',
    icon: MessageSquare,
    hint: 'Active conversation'
  },
  CONVERSATION_ENDING: {
    label: 'Ending',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    bar: 'bg-blue-400',
    icon: CheckCircle2,
    hint: 'Verify this is a real close'
  },
  OPT_OUT: {
    label: 'Opted Out',
    color: 'bg-red-100 text-red-800 border-red-300',
    bar: 'bg-red-400',
    icon: X,
    hint: 'Lead opted out'
  },
  NOT_INTERESTED: {
    label: 'Not Interested',
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    bar: 'bg-gray-400',
    icon: MessageSquare,
    hint: 'Not interested'
  },
  WRONG_NUMBER: {
    label: 'Wrong Number',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    bar: 'bg-orange-400',
    icon: AlertTriangle,
    hint: 'Wrong number'
  },
  HOSTILE: {
    label: 'Hostile',
    color: 'bg-red-100 text-red-800 border-red-300',
    bar: 'bg-red-400',
    icon: AlertTriangle,
    hint: 'Hostile response'
  }
};

const TEMP_COLORS = {
  hot:  'bg-red-100 text-red-700 border-red-300',
  warm: 'bg-orange-100 text-orange-700 border-orange-300',
  cold: 'bg-blue-100 text-blue-700 border-blue-300',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function LiveDot({ isLive }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      {isLive && (
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
      )}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isLive ? 'bg-green-500' : 'bg-gray-300'}`} />
    </span>
  );
}

function IntentBadge({ item }) {
  const key = item.fallback ? 'FALLBACK' : item.intent;
  const cfg = INTENT_CONFIG[key] ?? INTENT_CONFIG.NEUTRAL;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${cfg.color}`}
      title={cfg.hint}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function TimeAgo({ timestamp }) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const compute = () => {
      if (!timestamp) return setLabel('—');
      const secs = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
      if (secs < 60)   return setLabel(`${secs}s ago`);
      if (secs < 3600) return setLabel(`${Math.floor(secs / 60)}m ago`);
      return setLabel(`${Math.floor(secs / 3600)}h ago`);
    };
    compute();
    const id = setInterval(compute, 15000);
    return () => clearInterval(id);
  }, [timestamp]);
  return <span className="text-xs text-gray-400 whitespace-nowrap">{label}</span>;
}

function TimeSince({ timestamp }) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const compute = () => {
      if (!timestamp) return setLabel('—');
      const secs = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
      const mins = Math.floor(secs / 60);
      const hrs = Math.floor(mins / 60);
      if (hrs > 0) return setLabel(`${hrs}h ${mins % 60}m`);
      return setLabel(`${mins}m`);
    };
    compute();
    const id = setInterval(compute, 15000);
    return () => clearInterval(id);
  }, [timestamp]);
  return <span className="text-xs font-medium whitespace-nowrap">{label}</span>;
}

function TemperatureBadge({ temp }) {
  if (!temp) return null;
  const key = String(temp).toLowerCase();
  if (!TEMP_COLORS[key]) return null;
  const cls = TEMP_COLORS[key] ?? 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${cls}`}>
      {temp}
    </span>
  );
}

function ConversationThread({ leadId, tenantId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('messages')
        // status + error_code are selected only so wasNeverSent can read them. QC grades AI
        // copy, and a draft the wall clock killed before the send is copy no lead ever saw —
        // judging it teaches the wrong lesson about a message that had no audience.
        .select('direction, message_body, timestamp, status, error_code')
        .eq('lead_id', leadId)
        .eq('tenant_id', tenantId)
        .order('timestamp', { ascending: false })
        .limit(12);
      setMessages((data || []).filter((m) => !wasNeverSent(m)).reverse());
      setLoading(false);
    };
    load();
  }, [leadId, tenantId]);

  if (loading) return (
    <div className="px-4 py-3 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
      <RefreshCw className="w-3 h-3 animate-spin" /> Loading conversation…
    </div>
  );

  if (!messages.length) return (
    <div className="px-4 py-3 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
      No messages found.
    </div>
  );

  return (
    <div className="px-4 py-3 space-y-2 bg-gray-50 border-t border-gray-100">
      {messages.map((m, i) => {
        const isInbound = m.direction === 'inbound';
        return (
          <div key={i} className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-xs px-3 py-1.5 rounded-2xl text-sm leading-snug ${
              isInbound
                ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                : 'bg-indigo-600 text-white rounded-br-sm'
            }`}>
              {m.message_body}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Compliance Flags (collapsed) ────────────────────────────────────────────

function ComplianceFlags({ showTenantFilter, tenants }) {
  const [open, setOpen] = useState(false);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      let query = supabase
        .from('qc_flagged_leads')
        .select('*')
        .order('flagged_at', { ascending: false })
        .limit(100);

      const { data: testTenants } = await supabase
        .from('tenants')
        .select('id')
        .eq('is_test', true);
      const testIds = (testTenants || []).map(t => t.id);
      if (testIds.length > 0) {
        query = query.not('tenant_id', 'in', `(${testIds.join(',')})`);
      }

      const { data } = await query;
      setFlags(data || []);
      setLoading(false);
    };
    load();
  }, [open]);

  const getTenantName = (id) => tenants.find(t => t.id === id)?.name ?? '';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-semibold text-gray-700">Compliance Flags</span>
          <span className="text-xs text-gray-400">keyword / spam detection</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="bg-white border-t border-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 text-gray-300 animate-spin" />
            </div>
          ) : flags.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No compliance flags.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {flags.map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-sm">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                    item.severity === 'critical'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {item.severity}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                    {item.flag_type?.replace(/_/g, ' ')}
                  </span>
                  <span className="font-medium text-gray-800 truncate w-28">{item.lead_name}</span>
                  {showTenantFilter && (
                    <span className="text-xs text-gray-400 truncate w-24">{getTenantName(item.tenant_id)}</span>
                  )}
                  <span className="text-gray-600 truncate flex-1">{item.flagged_message}</span>
                  <button
                    onClick={() => window.open(`/lead/${item.lead_id}`, '_blank')}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Kanban Card Components ──────────────────────────────────────────────────

function KanbanCard({ lead, borderColor, bgColor, children, tenantName, showTenant }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-lg border shadow-sm transition-all cursor-pointer ${bgColor || 'bg-white'}`}
      style={{ borderLeft: `3px solid ${borderColor}` }}
      onClick={() => setExpanded(e => !e)}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-gray-800 truncate">{lead.name || 'Unknown'}</span>
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`/lead/${lead.id}`, '_blank'); }}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
            title="View lead"
          >
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
        {showTenant && tenantName && (
          <p className="text-xs text-indigo-400 mb-1">{tenantName}</p>
        )}
        {children}
      </div>
      {expanded && (
        <ConversationThread leadId={lead.id} tenantId={lead.tenant_id} />
      )}
    </div>
  );
}

function Col1Card({ lead, tenantName, showTenant }) {
  return (
    <KanbanCard lead={lead} borderColor="#6366f1" tenantName={tenantName} showTenant={showTenant}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500">
          <MessageSquare className="w-3 h-3 inline mr-0.5" />
          {lead._msgCountToday || 0} msgs today
        </span>
        {lead._latestIntent && (
          <IntentBadge item={lead._latestIntent} />
        )}
      </div>
      <div className="mt-1">
        <TimeAgo timestamp={lead._lastInboundAt || lead.last_message_at} />
      </div>
    </KanbanCard>
  );
}

function Col15Card({ lead, tenantName, showTenant }) {
  return (
    <KanbanCard lead={lead} borderColor="#3b82f6" tenantName={tenantName} showTenant={showTenant}>
      <div className="flex items-center gap-2 mb-1">
        <Phone className="w-3 h-3 text-blue-500" />
        <span className="text-xs font-medium text-blue-700">
          {Math.round((lead._callReadinessConfidence ?? 0) * 100)}% ready
        </span>
      </div>
      {lead._callReadinessRationale && (
        <p className="text-xs text-gray-400 italic truncate">{lead._callReadinessRationale}</p>
      )}
      <div className="mt-1">
        <TimeAgo timestamp={lead._callReadinessDetectedAt} />
      </div>
    </KanbanCard>
  );
}

function Col2Card({ lead, tenantName, showTenant, now }) {
  const msSince = lead.scheduling_mode_at ? now - new Date(lead.scheduling_mode_at).getTime() : 0;
  const hoursSince = msSince / 3600000;
  let agingBg = 'bg-white';
  let showPulse = false;

  if (hoursSince >= 2) {
    agingBg = 'bg-red-50';
    showPulse = true;
  } else if (hoursSince >= 1) {
    agingBg = 'bg-yellow-50';
  }

  return (
    <KanbanCard lead={lead} borderColor="#f97316" bgColor={agingBg} tenantName={tenantName} showTenant={showTenant}>
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-3 h-3 text-orange-500" />
        <TimeSince timestamp={lead.scheduling_mode_at} />
        {showPulse && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
        )}
        <TemperatureBadge temp={lead.latest_judge_temperature} />
      </div>
      {lead._lastInboundPreview && (
        <p className="text-xs text-gray-500 truncate">{lead._lastInboundPreview}</p>
      )}
    </KanbanCard>
  );
}

function Col3Card({ lead, tenantName, showTenant }) {
  const msgCount = lead._msgCountAtHandoff ?? 0;
  const lowIntel = msgCount < 4;
  const cardBg = lowIntel ? 'bg-yellow-50' : 'bg-green-50';

  return (
    <KanbanCard lead={lead} borderColor={lowIntel ? '#eab308' : '#22c55e'} bgColor={cardBg} tenantName={tenantName} showTenant={showTenant}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Flame className="w-3 h-3 text-green-600" />
        <TimeAgo timestamp={lead.marked_hot_at} />
        <TemperatureBadge temp={lead.latest_judge_temperature} />
        {lowIntel && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-200 text-yellow-800 border border-yellow-300">
            Low Intel
          </span>
        )}
      </div>
      {lead.escalation_reason && (
        <p className="text-xs text-gray-600 truncate mb-0.5">
          <span className="font-medium">Escalation:</span> {lead.escalation_reason}
        </p>
      )}
      {lead.hot_reason && (
        <p className="text-xs text-gray-500 truncate">
          <span className="font-medium">Reason:</span> {lead.hot_reason}
        </p>
      )}
      <p className="text-xs text-gray-400 mt-0.5">{msgCount} messages at handoff</p>
    </KanbanCard>
  );
}

// ─── Stale Conversations Accordion ───────────────────────────────────────────

function StaleConversations({ showTenantFilter, tenants, tenantFilter }) {
  const [open, setOpen] = useState(false);
  const [staleLeads, setStaleLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];

      // Step 1: Find most recent inbound message per lead (before today, within 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      let msgQuery = supabase
        .from('messages')
        .select('lead_id, timestamp')
        .eq('direction', 'inbound')
        .lt('timestamp', today)
        .gte('timestamp', thirtyDaysAgo)
        .order('timestamp', { ascending: false })
        .limit(500);

      if (tenantFilter && tenantFilter !== 'all') {
        msgQuery = msgQuery.eq('tenant_id', tenantFilter);
      }

      const { data: inboundMsgs } = await msgQuery;

      // Deduplicate: keep only the most recent inbound per lead
      const latestInboundByLead = new Map();
      for (const msg of (inboundMsgs || [])) {
        if (!latestInboundByLead.has(msg.lead_id)) {
          latestInboundByLead.set(msg.lead_id, msg.timestamp);
        }
      }

      // Exclude leads that also have an inbound message today (not stale)
      let todayMsgQuery = supabase
        .from('messages')
        .select('lead_id')
        .eq('direction', 'inbound')
        .gte('timestamp', today);

      if (tenantFilter && tenantFilter !== 'all') {
        todayMsgQuery = todayMsgQuery.eq('tenant_id', tenantFilter);
      }

      const { data: todayMsgs } = await todayMsgQuery;
      const hasInboundToday = new Set((todayMsgs || []).map(m => m.lead_id));

      const staleLeadIds = [...latestInboundByLead.keys()].filter(id => !hasInboundToday.has(id));
      if (staleLeadIds.length === 0) {
        setStaleLeads([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch lead details, filtering out terminal/opted-out/handed-off
      let leadQuery = supabase
        .from('leads')
        .select('id, name, tenant_id, status, latest_judge_temperature, last_message_at, scheduling_mode')
        .in('id', staleLeadIds.slice(0, 200))
        .is('marked_hot_at', null)
        .or('sms_opted_out.is.null,sms_opted_out.eq.false')
        .not('status', 'in', '("Won","Lost","dead","Disqualified")');

      // Exclude test tenants when viewing all
      if (!tenantFilter || tenantFilter === 'all') {
        const { data: testTenants } = await supabase
          .from('tenants')
          .select('id')
          .eq('is_test', true);
        const testIds = (testTenants || []).map(t => t.id);
        if (testIds.length > 0) {
          leadQuery = leadQuery.not('tenant_id', 'in', `(${testIds.join(',')})`);
        }
      }

      const { data: leads } = await leadQuery;

      // Step 3: Filter for leads with at least 1 inbound + 2 outbound total
      const qualified = [];
      for (const lead of (leads || [])) {
        const [{ count: inCount }, { count: outCount }] = await Promise.all([
          supabase.from('messages').select('*', { count: 'exact', head: true }).eq('lead_id', lead.id).eq('direction', 'inbound'),
          supabase.from('messages').select('*', { count: 'exact', head: true }).eq('lead_id', lead.id).eq('direction', 'outbound')
        ]);
        if ((inCount || 0) >= 1 && (outCount || 0) >= 2) {
          qualified.push({
            ...lead,
            _inCount: inCount,
            _outCount: outCount,
            _lastInboundAt: latestInboundByLead.get(lead.id)
          });
        }
        if (qualified.length >= 50) break;
      }

      // Sort by most recent inbound descending
      qualified.sort((a, b) => new Date(b._lastInboundAt) - new Date(a._lastInboundAt));
      setStaleLeads(qualified);
      setLoading(false);
    };
    load();
  }, [open, tenantFilter]);

  const getTenantName = (id) => tenants.find(t => t.id === id)?.name ?? '';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Stale Conversations</span>
          {staleLeads.length > 0 && (
            <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{staleLeads.length}</span>
          )}
          <span className="text-xs text-gray-400">inactive since yesterday or earlier</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="bg-white border-t border-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 text-gray-300 animate-spin" />
            </div>
          ) : staleLeads.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No stale conversations found.</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {staleLeads.map((lead) => (
                <div key={lead.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-sm">
                  <span className="font-medium text-gray-800 truncate w-32">{lead.name}</span>
                  {showTenantFilter && (
                    <span className="text-xs text-gray-400 truncate w-24">{getTenantName(lead.tenant_id)}</span>
                  )}
                  <TemperatureBadge temp={lead.latest_judge_temperature} />
                  {lead.scheduling_mode && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300">
                      Was Scheduling
                    </span>
                  )}
                  <span className="text-xs text-gray-400 flex-1">
                    Last active: <TimeAgo timestamp={lead.last_message_at} />
                  </span>
                  <span className="text-xs text-gray-400">{lead.status}</span>
                  <button
                    onClick={() => window.open(`/lead/${lead.id}`, '_blank')}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Column Wrapper ──────────────────────────────────────────────────────────

function KanbanColumn({ title, icon: Icon, count, borderColor, children }) {
  return (
    <div className="flex flex-col min-w-0 flex-1">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-gray-200 rounded-t-xl">
        <Icon className="w-4 h-4" style={{ color: borderColor }} />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <span className="ml-auto text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          {count}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gray-50/50 rounded-b-xl" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Data Fetchers ───────────────────────────────────────────────────────────

async function fetchLead(leadId) {
  const { data } = await supabase
    .from('leads')
    .select('id, name, tenant_id, status, scheduling_mode, scheduling_mode_at, marked_hot_at, sms_opted_out, latest_judge_temperature, latest_judge_confidence, latest_judge_reasoning, escalation_reason, hot_reason, last_message_at, next_followup_at, campaign_id')
    .eq('id', leadId)
    .single();
  return data;
}

async function fetchTodayMessageCounts(leadId) {
  const today = new Date().toISOString().split('T')[0];
  const { count: inbound } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('lead_id', leadId)
    .eq('direction', 'inbound')
    .gte('timestamp', today);
  const { count: outbound } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('lead_id', leadId)
    .eq('direction', 'outbound')
    .gte('timestamp', today);
  return { inbound: inbound || 0, outbound: outbound || 0 };
}

async function fetchLatestIntent(leadId) {
  const { data } = await supabase
    .from('intent_classifications')
    .select('intent, fallback, confidence, reasoning')
    .eq('lead_id', leadId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  return data;
}

async function fetchTotalMessageCount(leadId) {
  const { count } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('lead_id', leadId);
  return count || 0;
}

async function fetchLastInboundPreview(leadId) {
  const { data } = await supabase
    .from('messages')
    .select('message_body')
    .eq('lead_id', leadId)
    .eq('direction', 'inbound')
    .order('timestamp', { ascending: false })
    .limit(1)
    .single();
  return data?.message_body || null;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function QCQueue() {
  const { user } = useAuth();

  const [col1, setCol1]   = useState(new Map());   // In Conversation
  const [col15, setCol15] = useState(new Map());   // Call Ready
  const [col2, setCol2]   = useState(new Map());   // Scheduling
  const [col3, setCol3]   = useState(new Map());   // Handed Off

  const [isLive, setIsLive]               = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [loading, setLoading]             = useState(true);
  const [now, setNow]                     = useState(Date.now());

  // ── Refs — updated synchronously inside helpers, never via useEffect ──
  const col1Ref  = useRef(new Map());
  const col15Ref = useRef(new Map());
  const col2Ref  = useRef(new Map());
  const col3Ref  = useRef(new Map());

  const excludeRef = useRef(new Set());

  const showTenantFilter = user?.role === 'partner_admin' || user?.role === 'global_admin';
  const { data: tenants = [] } = useTenants({
    enabled: showTenantFilter,
    role: user?.role,
    tenantId: user?.tenant_id
  });
  const getTenantName = useCallback((id) => tenants.find(t => t.id === id)?.name ?? '', [tenants]);

  // Aging timer — recalculate every 60s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // ── State mutation helpers ──
  // Refs are updated synchronously BEFORE setState so subscription handlers
  // always see current positions regardless of React render timing.
  const addToCol1 = (lead) => {
    col1Ref.current.set(lead.id, lead);
    setCol1(prev => new Map(prev).set(lead.id, lead));
  };
  const addToCol15 = (lead, msg) => {
    const entry = {
      ...lead,
      _callReadinessConfidence: msg.call_readiness_confidence,
      _callReadinessRationale: msg.call_readiness_rationale,
      _callReadinessDetectedAt: msg.sent_at || msg.timestamp
    };
    col15Ref.current.set(lead.id, entry);
    setCol15(prev => new Map(prev).set(lead.id, entry));
  };
  const addToCol2 = (lead) => {
    col2Ref.current.set(lead.id, lead);
    setCol2(prev => new Map(prev).set(lead.id, lead));
  };
  const addToCol3 = (lead) => {
    col3Ref.current.set(lead.id, lead);
    setCol3(prev => new Map(prev).set(lead.id, lead));
  };

  const removeFromCol1 = (leadId) => {
    col1Ref.current.delete(leadId);
    setCol1(prev => { const n = new Map(prev); n.delete(leadId); return n; });
  };
  const removeFromCol15 = (leadId) => {
    col15Ref.current.delete(leadId);
    setCol15(prev => { const n = new Map(prev); n.delete(leadId); return n; });
  };
  const removeFromCol2 = (leadId) => {
    col2Ref.current.delete(leadId);
    setCol2(prev => { const n = new Map(prev); n.delete(leadId); return n; });
  };
  const removeFromCol3 = (leadId) => {
    col3Ref.current.delete(leadId);
    setCol3(prev => { const n = new Map(prev); n.delete(leadId); return n; });
  };

  // ── Page Load ──
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const tenantFilter = (!showTenantFilter && user?.tenant_id) ? user.tenant_id : null;

      // Fetch test tenant IDs to exclude from all queries
      let excludeTenantIds = [];
      if (!tenantFilter) {
        const { data: testTenants } = await supabase
          .from('tenants')
          .select('id')
          .eq('is_test', true);
        excludeTenantIds = (testTenants || []).map(t => t.id);
        excludeRef.current = new Set(excludeTenantIds);
      }

      // Query 1: Populate Col1 — active inbound leads today
      let q1 = supabase
        .from('messages')
        .select('lead_id, timestamp')
        .eq('direction', 'inbound')
        .gte('timestamp', today)
        .order('timestamp', { ascending: false });

      if (tenantFilter) {
        q1 = q1.eq('tenant_id', tenantFilter);
      } else if (excludeTenantIds.length > 0) {
        q1 = q1.not('tenant_id', 'in', `(${excludeTenantIds.join(',')})`);
      }

      // Query 2: Populate Col2 — scheduling mode (only recent, last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      let q2 = supabase
        .from('leads')
        .select('id, name, tenant_id, scheduling_mode_at, latest_judge_temperature, latest_judge_confidence, last_message_at, escalation_reason')
        .eq('scheduling_mode', true)
        .is('marked_hot_at', null)
        .or('sms_opted_out.is.null,sms_opted_out.eq.false')
        .gte('last_message_at', sevenDaysAgo)
        .order('scheduling_mode_at', { ascending: true });

      if (tenantFilter) {
        q2 = q2.eq('tenant_id', tenantFilter);
      } else if (excludeTenantIds.length > 0) {
        q2 = q2.not('tenant_id', 'in', `(${excludeTenantIds.join(',')})`);
      }

      // Query 3: Populate Col3 — handed off today
      let q3 = supabase
        .from('leads')
        .select('id, name, tenant_id, marked_hot_at, escalation_reason, hot_reason, latest_judge_temperature, latest_judge_confidence')
        .gte('marked_hot_at', today)
        .eq('handoff_triggered', true)
        .order('marked_hot_at', { ascending: false });

      if (tenantFilter) {
        q3 = q3.eq('tenant_id', tenantFilter);
      } else if (excludeTenantIds.length > 0) {
        q3 = q3.not('tenant_id', 'in', `(${excludeTenantIds.join(',')})`);
      }

      const [r1, r2, r3] = await Promise.all([q1, q2, q3]);

      // Process Col1: deduplicate by lead_id, then qualify
      const inboundMessages = r1.data || [];
      const seenLeadIds = new Set();
      const uniqueLeadIds = [];
      for (const msg of inboundMessages) {
        if (!seenLeadIds.has(msg.lead_id)) {
          seenLeadIds.add(msg.lead_id);
          uniqueLeadIds.push(msg.lead_id);
        }
      }

      // Build col2 and col3 maps first so we can exclude them from col1
      const col2Map = new Map();
      for (const lead of (r2.data || [])) {
        const preview = await fetchLastInboundPreview(lead.id);
        col2Map.set(lead.id, { ...lead, _lastInboundPreview: preview });
      }

      const col3Map = new Map();
      for (const lead of (r3.data || [])) {
        const msgCount = await fetchTotalMessageCount(lead.id);
        col3Map.set(lead.id, { ...lead, _msgCountAtHandoff: msgCount });
      }

      const col1Map = new Map();
      for (const leadId of uniqueLeadIds) {
        // Skip if already in col2 or col3
        if (col2Map.has(leadId) || col3Map.has(leadId)) continue;

        const lead = await fetchLead(leadId);
        if (!lead || lead.sms_opted_out || lead.scheduling_mode || lead.marked_hot_at) continue;

        const counts = await fetchTodayMessageCounts(leadId);
        if (counts.inbound < 2 || counts.outbound < 2) continue;

        const latestIntent = await fetchLatestIntent(leadId);
        if (latestIntent && TERMINAL_INTENTS.has(latestIntent.intent)) continue;

        col1Map.set(leadId, {
          ...lead,
          _msgCountToday: counts.inbound + counts.outbound,
          _latestIntent: latestIntent,
          _lastInboundAt: inboundMessages.find(m => m.lead_id === leadId)?.timestamp
        });
      }

      setCol1(col1Map);
      setCol2(col2Map);
      setCol3(col3Map);
      setCol15(new Map()); // Col1.5 only populates via realtime
      setLoading(false);
    };

    loadAll();
  }, [showTenantFilter, user?.tenant_id]);

  // ── Realtime Subscriptions ──
  useEffect(() => {
    const tenantFilter = (!showTenantFilter && user?.tenant_id) ? user.tenant_id : null;

    // ── Subscription 1: messages INSERT ──
    const uid = Math.random().toString(36).slice(2, 8);
    const msgChannel = supabase
      .channel(`kanban-messages-feed-${uid}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          const msg = payload.new;
          const leadId = msg.lead_id;

          try {
            // Tenant filter
            if (tenantFilter && msg.tenant_id !== tenantFilter) return;
            if (!tenantFilter && excludeRef.current.has(msg.tenant_id)) return;

            // Step 1 — fetch latest intent
            const { data: intentRow } = await supabase
              .from('intent_classifications')
              .select('intent')
              .eq('lead_id', leadId)
              .order('created_at', { ascending: false })
              .limit(1)
              .single();

            // Step 2 — terminal intent: remove from col1/col1.5 and stop
            if (intentRow && TERMINAL_INTENTS.has(intentRow.intent)) {
              removeFromCol1(leadId);
              removeFromCol15(leadId);
              return;
            }

            // Step 3 — if lead already graduated to col2/col3, nothing to do
            if (col2Ref.current.has(leadId) || col3Ref.current.has(leadId)) return;

            // Step 4 — outbound with call_readiness_detected: col1 → col1.5
            if (msg.direction === 'outbound' && msg.call_readiness_detected === true) {
              if (col15Ref.current.has(leadId)) return;

              const { data: lead } = await supabase
                .from('leads')
                .select('id, name, tenant_id, campaign_id, status, latest_judge_temperature, last_message_at, scheduling_mode, marked_hot_at, sms_opted_out')
                .eq('id', leadId)
                .single();

              if (!lead || lead.sms_opted_out || lead.scheduling_mode || lead.marked_hot_at) return;

              removeFromCol1(leadId);
              addToCol15(lead, msg);
              return;
            }

            // Step 5 — evaluate col1 qualification (both inbound and outbound)
            if (col1Ref.current.has(leadId) || col15Ref.current.has(leadId)) return;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const { data: todayMessages } = await supabase
              .from('messages')
              .select('direction')
              .eq('lead_id', leadId)
              .gte('timestamp', today.toISOString());

            const inboundCount = (todayMessages || []).filter(m => m.direction === 'inbound').length;
            const outboundCount = (todayMessages || []).filter(m => m.direction === 'outbound').length;

            if (inboundCount < 2 || outboundCount < 2) return;

            const { data: lead } = await supabase
              .from('leads')
              .select('id, name, tenant_id, campaign_id, status, latest_judge_temperature, last_message_at, scheduling_mode, marked_hot_at, sms_opted_out')
              .eq('id', leadId)
              .single();

            if (!lead || lead.sms_opted_out || lead.scheduling_mode || lead.marked_hot_at) return;

            addToCol1(lead);

          } catch (err) {
            console.error('Messages INSERT handler error:', err);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setIsLive(true);
        else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') setIsLive(false);
      });

    // ── Subscription 2: leads UPDATE ──
    // Do NOT use payload.old — with default replica identity it only has the PK.
    // Use refs to determine current board state instead.
    const leadChannel = supabase
      .channel(`kanban-leads-feed-${uid}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'leads' },
        async (payload) => {
          const lead = payload.new;
          const leadId = lead.id;

          try {
            // Tenant filter
            if (tenantFilter && lead.tenant_id !== tenantFilter) return;
            if (!tenantFilter && excludeRef.current.has(lead.tenant_id)) return;

            // scheduling_mode = true → move to col2
            if (lead.scheduling_mode === true && !col2Ref.current.has(leadId) && !col3Ref.current.has(leadId)) {
              removeFromCol1(leadId);
              removeFromCol15(leadId);
              addToCol2(lead);
              return;
            }

            // scheduling_mode = false → remove from col2 if present
            if (lead.scheduling_mode === false && col2Ref.current.has(leadId)) {
              removeFromCol2(leadId);
              return;
            }

            // marked_hot_at written → move to col3
            if (lead.marked_hot_at && !col3Ref.current.has(leadId)) {
              removeFromCol1(leadId);
              removeFromCol15(leadId);
              removeFromCol2(leadId);

              const { count } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .eq('lead_id', leadId);

              addToCol3({ ...lead, _msgCountAtHandoff: count ?? 0 });
              return;
            }

            // marked_hot_at cleared → remove from col3 if present
            if (!lead.marked_hot_at && col3Ref.current.has(leadId)) {
              removeFromCol3(leadId);
              return;
            }

            // sms_opted_out = true → remove from all columns
            if (lead.sms_opted_out === true) {
              removeFromCol1(leadId);
              removeFromCol15(leadId);
              removeFromCol2(leadId);
              return;
            }

          } catch (err) {
            console.error('Leads UPDATE handler error:', err);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setIsLive(true);
        else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') setIsLive(false);
      });

    return () => {
      supabase.removeChannel(msgChannel);
      supabase.removeChannel(leadChannel);
      setIsLive(false);
    };
  }, [selectedTenant, showTenantFilter, user?.tenant_id]);

  // ── Render ──
  const col1Array  = [...col1.values()];
  const col15Array = [...col15.values()];
  const col2Array  = [...col2.values()];
  const col3Array  = [...col3.values()];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">Live Campaign Monitor</h1>
                <LiveDot isLive={isLive} />
                <span className="text-xs text-gray-400">{isLive ? 'Live' : 'Connecting…'}</span>
              </div>
              <p className="text-xs text-gray-400">
                {col1Array.length + col15Array.length + col2Array.length + col3Array.length} leads tracked
              </p>
            </div>
          </div>

          {showTenantFilter && (
            <div className="relative">
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="appearance-none text-sm border border-gray-300 rounded-lg pl-8 pr-7 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="all">All Tenants</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
            <p className="text-sm text-gray-400">Loading campaign data…</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col px-4 py-4 gap-4 overflow-hidden">
          <div className="flex gap-3 flex-1 overflow-hidden">
            {/* Column 1 — In Conversation */}
            <KanbanColumn title="In Conversation" icon={MessageSquare} count={col1Array.length} borderColor="#6366f1">
              {col1Array.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No active conversations</p>
              ) : (
                col1Array.map(lead => (
                  <Col1Card
                    key={lead.id}
                    lead={lead}
                    tenantName={getTenantName(lead.tenant_id)}
                    showTenant={showTenantFilter}
                  />
                ))
              )}
            </KanbanColumn>

            {/* Column 1.5 — Call Ready */}
            <KanbanColumn title="Call Ready" icon={Phone} count={col15Array.length} borderColor="#3b82f6">
              {col15Array.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">Waiting for call readiness…</p>
              ) : (
                col15Array.map(lead => (
                  <Col15Card
                    key={lead.id}
                    lead={lead}
                    tenantName={getTenantName(lead.tenant_id)}
                    showTenant={showTenantFilter}
                  />
                ))
              )}
            </KanbanColumn>

            {/* Column 2 — Scheduling */}
            <KanbanColumn title="Scheduling" icon={Calendar} count={col2Array.length} borderColor="#f97316">
              {col2Array.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No leads in scheduling mode</p>
              ) : (
                col2Array.map(lead => (
                  <Col2Card
                    key={lead.id}
                    lead={lead}
                    tenantName={getTenantName(lead.tenant_id)}
                    showTenant={showTenantFilter}
                    now={now}
                  />
                ))
              )}
            </KanbanColumn>

            {/* Column 3 — Handed Off */}
            <KanbanColumn title="Handed Off" icon={Flame} count={col3Array.length} borderColor="#22c55e">
              {col3Array.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No handoffs today</p>
              ) : (
                col3Array.map(lead => (
                  <Col3Card
                    key={lead.id}
                    lead={lead}
                    tenantName={getTenantName(lead.tenant_id)}
                    showTenant={showTenantFilter}
                  />
                ))
              )}
            </KanbanColumn>
          </div>

          {/* Bottom accordions */}
          <div className="flex-shrink-0 space-y-3">
            <StaleConversations
              showTenantFilter={showTenantFilter}
              tenants={tenants}
              tenantFilter={selectedTenant}
            />
            <ComplianceFlags showTenantFilter={showTenantFilter} tenants={tenants} />
          </div>
        </div>
      )}
    </div>
  );
}
